import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FileText } from 'lucide-react';
import { reportsApi } from '@/api/reports';
import { Report, ReportStatus, ReportMode } from '@/types';
import { Button, Card, EmptyState, Skeleton, cn } from '@/ui';
import { useInspectorProfile } from '@/contexts/InspectorProfileContext';

const STATUS_CHIP: Record<ReportStatus, { label: string; className: string; icon?: React.ReactNode }> = {
  draft: {
    label: 'Draft',
    className: 'border border-dashed border-[var(--warn-500)] text-[var(--warn-500)]',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-[var(--brand-50)] text-[var(--brand-700)] border border-[var(--brand-200)]',
  },
  completed: {
    label: 'Delivered',
    className: 'bg-[var(--brand-500)] text-white border border-[var(--brand-500)]',
    icon: <Check className="h-3 w-3 shrink-0" aria-hidden />,
  },
  sent: {
    label: 'Delivered',
    className: 'bg-[var(--brand-500)] text-white border border-[var(--brand-500)]',
    icon: <Check className="h-3 w-3 shrink-0" aria-hidden />,
  },
};

const MODE_LABEL: Record<ReportMode, string> = {
  buyer:  "Buyer's Inspection",
  seller: 'Pre-Listing',
};

const formatDate = (date: Date | string) =>
  new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const formatRelative = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

const ReportList: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useInspectorProfile();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await reportsApi.getReports(1, 50);
        if (result.data) {
          setReports(result.data);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-heading-lg font-display text-ink">Reports</h1>
        <p className="text-sm text-ink-muted mt-1">
          Generated from completed inspections.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card p-4 flex items-start gap-4">
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <Skeleton width="w-3/5" height="h-4" />
                <Skeleton width="w-2/5" height="h-3" />
                <Skeleton width="w-1/4" height="h-3" />
              </div>
              <Skeleton width="w-20" height="h-5" shape="pill" />
            </div>
          ))}
        </div>
      ) : reports.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-10 w-10" />}
          title="No reports yet"
          description='Complete an inspection and tap "Generate Report" to get started.'
          action={<Button onClick={() => navigate('/inspections')}>Go to Inspections</Button>}
        />
      ) : (
        <div className="space-y-3">
          {reports.map((report) => {
            const statusCfg = STATUS_CHIP[report.status] ?? STATUS_CHIP.draft;
            const feeNum = report.feeAmount != null ? Number(report.feeAmount) : 0;
            const awaitingPayment =
              (profile?.requirePaymentBeforeDelivery ?? true) &&
              Number.isFinite(feeNum) &&
              feeNum > 0 &&
              !report.paymentReceivedAt &&
              report.status !== 'sent';
            return (
              <Card
                key={report.id}
                as="button"
                onClick={() => navigate(`/reports/${report.id}`)}
                density="compact"
                className="w-full text-left hover:border-brand/40 hover:shadow-card transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">
                      {report.propertyAddress}
                    </p>
                    {report.clientName && (
                      <p className="text-sm text-ink-muted mt-0.5">{report.clientName}</p>
                    )}
                    <p className="text-caption text-ink-faint mt-1">
                      {formatDate(report.inspectionDate)}
                      {report.mode && <span className="mx-2 text-ink-faint/50">·</span>}
                      {report.mode && <span>{MODE_LABEL[report.mode]}</span>}
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-caption font-medium whitespace-nowrap',
                      statusCfg.className,
                    )}>
                      {statusCfg.icon}
                      {statusCfg.label}
                    </span>
                    {awaitingPayment && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-caption font-medium whitespace-nowrap border border-[var(--warn-400)] text-[var(--warn-700)] bg-[var(--warn-50)]">
                        Awaiting payment
                      </span>
                    )}
                    {typeof report.viewsCount === 'number' && report.viewsCount > 0 && (
                      <span className="text-caption text-ink-faint nums-tabular">
                        Opened {report.viewsCount}×
                        {report.lastViewedAt ? ` · ${formatRelative(report.lastViewedAt)}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportList;
